import { useContext, useState, useMemo } from 'react'
import { Box, MenuItem, Select, Typography, Collapse, IconButton } from '@mui/material'
import Language from '@mui/icons-material/Language'
import ExpandMore from '@mui/icons-material/ExpandMore'
import ExpandLess from '@mui/icons-material/ExpandLess'
import QuizOutlined from '@mui/icons-material/QuizOutlined'
import SectionComponent from './SectionComponent'
import { UnsecureSetupContext } from 'src/consumer/context/UnsecureSetupContext'
import styled from 'styled-components'

const SectionHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, rgba(103, 121, 227, 0.08) 0%, rgba(103, 121, 227, 0.04) 100%);
  border-radius: 12px;
  margin-bottom: ${(props) => (props.$expanded ? '20px' : '0')};
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid rgba(103, 121, 227, 0.15);

  &:hover {
    background: linear-gradient(135deg, rgba(103, 121, 227, 0.12) 0%, rgba(103, 121, 227, 0.06) 100%);
    border-color: rgba(103, 121, 227, 0.25);
  }
`

const SectionTitle = styled(Typography)`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 12px;
`

const AnswersBadge = styled.span`
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
  background: ${(props) => (props.$hasAnswers ? 'rgba(16, 185, 129, 0.1)' : 'rgba(148, 163, 184, 0.1)')};
  color: ${(props) => (props.$hasAnswers ? '#059669' : '#64748b')};
  border: 1px solid ${(props) => (props.$hasAnswers ? 'rgba(16, 185, 129, 0.3)' : 'rgba(148, 163, 184, 0.3)')};
`

const CompactLanguageSelector = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: rgba(103, 121, 227, 0.04);
  border: 1px solid rgba(103, 121, 227, 0.15);
  border-radius: 8px;
  margin-bottom: 16px;
`

const SectionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export default function ScreeningSectionsAndQuestions({
  sections,
  answers,
  setAnswers,
  patientInfo,
}) {
  const { unSecureSetupData } = useContext(UnsecureSetupContext)
  
  // Determine best default language: patient's preferred language if questions exist in it, otherwise English
  const defaultLanguage = useMemo(() => {
    const patientLang = patientInfo?.defaultCommunicationLanguage
    if (!patientLang || patientLang === 'en') return 'en'
    
    // Check if any question has translation in patient's language
    const hasTranslationsInPatientLang = sections?.some(section => 
      section?.questions?.some(question => 
        question?.translations?.[patientLang]?.questionText
      )
    )
    
    return hasTranslationsInPatientLang ? patientLang : 'en'
  }, [sections, patientInfo?.defaultCommunicationLanguage])
  
  const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage)
  
  // Check if there are any filled answers
  const hasFilledAnswers = useMemo(() => {
    return answers && answers.length > 0 && answers.some(a => a?.response)
  }, [answers])
  
  // Expand by default only if there are filled answers
  const [isExpanded, setIsExpanded] = useState(hasFilledAnswers)
  
  // Count total questions and answered questions
  const { totalQuestions, answeredQuestions } = useMemo(() => {
    let total = 0
    let answered = 0
    sections?.forEach(section => {
      section?.questions?.forEach(question => {
        total++
        if (answers?.find(a => a?.questionId === question?.id && a?.response)) {
          answered++
        }
      })
    })
    return { totalQuestions: total, answeredQuestions: answered }
  }, [sections, answers])
  function handleChangeAnswer(questionId, sectionId, answerData, question = null, section = null) {
    setAnswers((prev) => {
      let answers = prev
      let currentQuestionsAnswer = answers?.find((answer) => answer?.questionId === questionId)
      if (!currentQuestionsAnswer) {
        currentQuestionsAnswer = {
          questionId: questionId,
          ...answerData,
        }
      } else {
        currentQuestionsAnswer = {
          ...currentQuestionsAnswer,
          ...answerData,
        }
      }
      
      // Enrich answer with question data if available
      if (question) {
        currentQuestionsAnswer = {
          ...currentQuestionsAnswer,
          questionText: question?.questionText || currentQuestionsAnswer?.questionText,
          shortQuestionText: question?.shortQuestionText || question?.questionText || currentQuestionsAnswer?.shortQuestionText,
          translations: question?.translations || currentQuestionsAnswer?.translations || {},
        }
      }
      
      // Add section info if available
      if (section) {
        currentQuestionsAnswer = {
          ...currentQuestionsAnswer,
          sectionId: sectionId,
          sectionTitle: section?.title || currentQuestionsAnswer?.sectionTitle,
        }
      } else {
        currentQuestionsAnswer = {
          ...currentQuestionsAnswer,
          sectionId: sectionId,
        }
      }
      
      answers = answers?.filter((answer) => answer?.questionId !== questionId)
      answers = [...(answers || []), currentQuestionsAnswer]
      return answers
    })
  }

  function getQuestionAnswer(questionId) {
    return answers?.find((answer) => answer?.questionId === questionId)
  }

  return (
    <div>
      {/* Collapsible Header */}
      <SectionHeader 
        $expanded={isExpanded}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <SectionTitle>
          <QuizOutlined sx={{ color: '#6779e3', fontSize: 24 }} />
          Screening Questions
          <AnswersBadge $hasAnswers={answeredQuestions > 0}>
            {answeredQuestions}/{totalQuestions} answered
          </AnswersBadge>
        </SectionTitle>
        <IconButton 
          size="small" 
          sx={{ color: '#6779e3' }}
          onClick={(e) => {
            e.stopPropagation()
            setIsExpanded(!isExpanded)
          }}
        >
          {isExpanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </SectionHeader>
      
      {/* Collapsible Content */}
      <Collapse in={isExpanded}>
        <CompactLanguageSelector>
          <Language sx={{ color: '#6779e3', fontSize: 18 }} />
          <Typography sx={{ fontSize: 13, color: '#64748b', whiteSpace: 'nowrap' }}>
            Language:
          </Typography>
          <Select
            size="small"
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
            sx={{
              fontSize: 13,
              minWidth: 120,
              '& .MuiSelect-select': {
                py: 0.5,
                px: 1.5,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(103, 121, 227, 0.2)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(103, 121, 227, 0.4)',
              },
              borderRadius: '6px',
              background: '#fff',
            }}
          >
            {unSecureSetupData?.appLanguages?.map((option) => (
              <MenuItem key={option?.id} value={option?.id} sx={{ fontSize: 13 }}>
                {option?.language || option?.title || option?.localLanguageName}
              </MenuItem>
            ))}
          </Select>
        </CompactLanguageSelector>
        <SectionsContainer>
          {sections?.map((section) => (
            <SectionComponent
              key={section?.id}
              sectionObject={section}
              getQuestionAnswer={getQuestionAnswer}
              handleChangeAnswer={handleChangeAnswer}
              selectedLanguage={selectedLanguage}
            />
          ))}
        </SectionsContainer>
      </Collapse>
    </div>
  )
}
